package one.devsbox.www.ui.cmd

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import one.devsbox.commons.ads.CmdController
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.toComposeImageBitmap
import one.devsbox.shared.msg.MessageBody
import one.devsbox.www.io.Repository
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import kotlin.time.Clock
import org.khronos.webgl.Int8Array

@VM
class CmdVM : NavigationViewModel() {
    private lateinit var ctx: CmdCtx
    private var controller: CmdController? = null

    var input by mutableStateOf("")
    val logs = mutableStateListOf<CmdLog>()

    var state by mutableStateOf<DataState<Unit>>(DataState.Loading())
        private set

    var toastMessage by mutableStateOf<String?>(null)

    fun showToast(msg: String) {
        toastMessage = msg
        scope.launch {
            kotlinx.coroutines.delay(2000)
            if (toastMessage == msg) {
                toastMessage = null
            }
        }
    }

    data class CmdLog(
        val command: String,
        val output: List<String>,
        val error: List<String>,
        val isOk: Boolean,
        val isPending: Boolean,
        val screenshot: ImageBitmap? = null,
        val screenshotBytes: ByteArray? = null,
        val sentTime: Long = Clock.System.now().toEpochMilliseconds(),
        val receivedTime: Long? = null
    )

    interface Action {
        fun sendCommand()
        fun clearLogs()
        fun reconnect()
        fun reset()
        fun back()
        fun takeScreenshot()
        fun copyScreenshotToClipboard(log: CmdLog)
        fun cancelPendingCommands()
    }

    val action = object : Action {
        override fun sendCommand() {
            val cmdText = input.trim()
            if (cmdText.isEmpty()) return
            input = ""
            val newLog = CmdLog(
                command = cmdText,
                output = emptyList(),
                error = emptyList(),
                isOk = true,
                isPending = true
            )
            logs.add(0, newLog)
            scope.launch {
                runCatching {
                    controller?.send(cmdText)
                }.onFailure {
                    val idx = logs.indexOf(newLog)
                    if (idx != -1) {
                        logs[idx] = newLog.copy(
                            isPending = false,
                            isOk = false,
                            error = listOf(it.message ?: "Failed to send command"),
                            receivedTime = Clock.System.now().toEpochMilliseconds()
                        )
                    }
                }
            }
        }

        override fun clearLogs() {
            logs.clear()
        }

        override fun reconnect() {
            state = DataState.Loading()
            controller?.reconnect()
        }

        override fun reset() {
            scope.launch {
                runCatching {
                    controller?.reset()
                }
            }
        }

        override fun cancelPendingCommands() {
            for (i in logs.indices) {
                val log = logs[i]
                if (log.isPending) {
                    logs[i] = log.copy(
                        isPending = false,
                        isOk = false,
                        error = listOf("Canceled"),
                        receivedTime = Clock.System.now().toEpochMilliseconds()
                    )
                }
            }
            reset()
        }

        override fun back() {
            ctx.nav.up()
        }

        override fun takeScreenshot() {
            val newLog = CmdLog(
                command = "screenshot",
                output = emptyList(),
                error = emptyList(),
                isOk = true,
                isPending = true,
                screenshot = null
            )
            logs.add(0, newLog)
            scope.launch {
                runCatching {
                    val bytes = Repository.ads.device.screenshot(ctx.args.id)
                    val bitmap =
                        org.jetbrains.skia.Image.makeFromEncoded(bytes).toComposeImageBitmap()
                    Pair(bitmap, bytes)
                }.onSuccess { result ->
                    val idx = logs.indexOf(newLog)
                    if (idx != -1) {
                        logs[idx] = newLog.copy(
                            isPending = false,
                            screenshot = result.first,
                            screenshotBytes = result.second,
                            receivedTime = Clock.System.now().toEpochMilliseconds()
                        )
                    }
                }.onFailure {
                    it.printStackTrace()
                    val idx = logs.indexOf(newLog)
                    if (idx != -1) {
                        logs[idx] = newLog.copy(
                            isPending = false,
                            isOk = false,
                            error = listOf(it.message ?: "Failed to capture screenshot"),
                            receivedTime = Clock.System.now().toEpochMilliseconds()
                        )
                    }
                }
            }
        }

        override fun copyScreenshotToClipboard(log: CmdLog) {
            val bytes = log.screenshotBytes ?: return
            runCatching {
                copyImageToClipboard(bytes)
                showToast("Screenshot copied to clipboard")
            }.onFailure {
                showToast("Failed to copy screenshot")
            }
        }
    }

    fun setup(ctx: CmdCtx) {
        this.ctx = ctx
        if (state is DataState.Loading) {
            initController()
        }
    }

    private fun initController() {
        scope.launch {
            runCatching {
                val ctrl = Repository.ads.device.cmd(ctx.args.id)
                controller = ctrl
                ctrl.onMessage { cmdResult ->
                    val pendingIndex = logs.indexOfLast { it.isPending }
                    if (pendingIndex != -1) {
                        logs[pendingIndex] = logs[pendingIndex].copy(
                            output = cmdResult.out,
                            error = cmdResult.err,
                            isOk = cmdResult.ok,
                            isPending = false,
                            receivedTime = Clock.System.now().toEpochMilliseconds()
                        )
                    } else {
                        val nowTime = Clock.System.now().toEpochMilliseconds()
                        logs.add(
                            0,
                            CmdLog(
                                command = cmdResult.msg.firstOrNull() ?: "Remote command feedback",
                                output = cmdResult.out,
                                error = cmdResult.err,
                                isOk = cmdResult.ok,
                                isPending = false,
                                sentTime = nowTime,
                                receivedTime = nowTime
                            )
                        )
                    }
                }
                ctrl.onClose {
                    state = DataState.Error("Disconnected from device")
                }
                ctrl.onOpen {
                    state = DataState.Result(Unit)
                }
                ctrl.onError {
                    state = DataState.Error(it.message ?: "Connection error")
                }
            }.onFailure {
                state = DataState.Error(it.message ?: "Failed to initialize CmdController")
            }
        }
    }

    override fun onRelease() {
        controller?.close()
    }
}

private fun copyImageToClipboard(bytes: ByteArray) {
    val int8Array = bytes.unsafeCast<Int8Array>()
    js(
        """
        var blob = new Blob([int8Array], { type: 'image/webp' });
        var url = URL.createObjectURL(blob);
        var img = new Image();
        img.onload = function() {
            var canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            var ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(function(pngBlob) {
                URL.revokeObjectURL(url);
                if (!pngBlob) {
                    console.error('Failed to convert image to PNG');
                    return;
                }
                if (navigator.clipboard && window.ClipboardItem) {
                    var item = new ClipboardItem({ 'image/png': pngBlob });
                    navigator.clipboard.write([item]).then(function() {
                        console.log('Image copied to clipboard as PNG');
                    }).catch(function(err) {
                        console.error('Failed to copy image: ', err);
                    });
                } else {
                    console.error('Clipboard API not supported');
                }
            }, 'image/png');
        };
        img.onerror = function() {
            URL.revokeObjectURL(url);
            console.error('Failed to load image for clipboard copy');
        };
        img.src = url;
    """
    )
}
